import { EsaMembershipCreditBalancesQuery } from "@outschool/gql-frontend-generated";
import { renderCurrencyString } from "@outschool/localization";
import {
  ApolloError,
  gql,
  useQueryWithPreviousData,
} from "@outschool/ui-apollo";
import { useSession } from "@outschool/ui-auth";
import React from "react";

export const esaMembershipCreditBalanceQuery = gql`
  query EsaMembershipCreditBalances {
    esaMembershipCreditBalances {
      ...EsaMembershipCreditBalancesFragment
    }
  }

  fragment EsaMembershipCreditBalancesFragment on EsaMembershipCreditBalance {
    uid
    balance
    openToBuy
    learner {
      uid
      name
      age
    }
    fundingProgram {
      abbreviation
      name
      isClassWallet
    }
  }
`;

export type EsaCreditBalance = {
  balance: number;
  openToBuy: number;
  age: number;
  name: string;
  learnerUid: string;
  programName: string;
  programAbbreviation: string;
  isClassWallet: boolean;
  uid: string;
};

type EsaMembershipCreditBalancesResult = {
  hasEsaMembershipCreditBalances: boolean;
  creditBalances: EsaCreditBalance[];
  totalBalanceCents: number;
  totalOpenToBuyCents: number;
  loading: boolean;
  error?: ApolloError;
  refetch: () => unknown;
};

function esaCreditBalanceHasFunds(
  balance: Pick<EsaCreditBalance, "balance" | "openToBuy">,
) {
  return balance.balance > 0 || balance.openToBuy > 0;
}

export function formatEsaWalletCents(
  cents: number,
  showCurrencyCode: boolean = true,
) {
  return renderCurrencyString({
    priceInCents: cents,
    showCurrencyCode,
    displayNoCents: true,
  });
}

export const useEsaMembershipCreditBalances =
  (): EsaMembershipCreditBalancesResult => {
    const { isLoggedIn } = useSession();

    const { data, loading, error, refetch } =
      useQueryWithPreviousData<EsaMembershipCreditBalancesQuery>(
        esaMembershipCreditBalanceQuery,
        {
          skip: !isLoggedIn,
        },
      );

    const creditBalances = React.useMemo(() => {
      const creditBalances = data?.esaMembershipCreditBalances ?? [];
      return creditBalances.map<EsaCreditBalance>(creditBalance => ({
        balance: creditBalance.balance,
        openToBuy: creditBalance.openToBuy,
        name: creditBalance.learner.name!,
        age: creditBalance.learner.age!,
        learnerUid: creditBalance.learner.uid!,
        programName: creditBalance.fundingProgram.name!,
        programAbbreviation: creditBalance.fundingProgram.abbreviation!,
        isClassWallet: creditBalance.fundingProgram.isClassWallet!,
        uid: creditBalance.uid,
      }));
    }, [data]);

    const totalBalanceCents = React.useMemo(
      () => creditBalances.reduce((sum, balance) => sum + balance.balance, 0),
      [creditBalances],
    );

    const totalOpenToBuyCents = React.useMemo(
      () => creditBalances.reduce((sum, balance) => sum + balance.openToBuy, 0),
      [creditBalances],
    );

    return {
      hasEsaMembershipCreditBalances: creditBalances.length > 0,
      creditBalances,
      totalBalanceCents,
      totalOpenToBuyCents,
      loading,
      error,
      refetch,
    };
  };

export function useLearnerHasEsaFunds(
  learnerUid: string | null | undefined,
  { hasEsaMembership }: { hasEsaMembership?: boolean | null } = {},
) {
  const balances = useEsaMembershipCreditBalances();

  const hasEsaFunds = React.useMemo(
    () =>
      Boolean(
        learnerUid &&
          balances.creditBalances.some(
            balance =>
              balance.learnerUid === learnerUid &&
              esaCreditBalanceHasFunds(balance),
          ),
      ),
    [balances.creditBalances, learnerUid],
  );

  return {
    hasEsaFunds,
    shouldLockNames: Boolean(
      hasEsaFunds || ((balances.loading || balances.error) && hasEsaMembership),
    ),
    loading: balances.loading,
    error: balances.error,
  };
}

type EsaMembershipCredit = ReturnType<typeof useEsaMembershipCreditBalances>;

export type EsaMembershipCreditBalance =
  EsaMembershipCredit["creditBalances"][number];
